import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@scale/scaleui';
import { twMerge } from 'tailwind-merge';
import TutorialMarkdown from './TutorialMarkdown';
import { TutorialMessageType } from './hooks/useTutorialStore';

interface TutorialMessageProps {
  message: string;
  messageType: TutorialMessageType | undefined;
  highlightControl?: JSX.Element | null;
  acknowledgeControl: JSX.Element;
}

const TutorialMessage: React.FC<TutorialMessageProps> = ({
  message,
  messageType,
  highlightControl,
  acknowledgeControl,
}) => {
  const title =
    messageType === TutorialMessageType.SUCCESS
      ? 'Correct!'
      : messageType === TutorialMessageType.ERROR
      ? 'Not quite...'
      : 'Instructional Info';

  const titleTextColorClassName = twMerge(
    'flex flex-row gap-1 items-center',
    messageType === TutorialMessageType.SUCCESS && '!text-success-700',
    messageType === TutorialMessageType.ERROR && '!text-danger-700',
    messageType === TutorialMessageType.INFO && '!text-info-700',
  );

  return (
    <div
      className={twMerge(
        `w-full p-4 pt-3 rounded-md mt-2 !ring-0`,
        messageType === TutorialMessageType.SUCCESS && 'bg-success-50',
        messageType === TutorialMessageType.ERROR && 'bg-danger-50',
        messageType === TutorialMessageType.INFO && 'bg-info-50',
      )}
    >
      <div
        className={twMerge(
          'w-full flex flex-row justify-between items-center',
          titleTextColorClassName,
        )}
      >
        <div className="flex flex-row gap-1 items-center">
          <FontAwesomeIcon
            className="p-1"
            icon={
              messageType === TutorialMessageType.SUCCESS
                ? ['fas', 'hexagon-check']
                : messageType === TutorialMessageType.ERROR
                ? ['fas', 'xmark-hexagon']
                : ['fas', 'lightbulb-on']
            }
          />
          <Typography.H5 className={titleTextColorClassName}>{title}</Typography.H5>
        </div>

        <div className="space-x-2">
          {highlightControl}
          {acknowledgeControl}
        </div>
      </div>
      <div className={'block'}>
        <TutorialMarkdown
          className={twMerge(
            messageType === TutorialMessageType.SUCCESS && '[&>*]:!text-success-700',
            messageType === TutorialMessageType.ERROR && '[&>*]:!text-danger-700',
            messageType === TutorialMessageType.INFO && '[&>*]:!text-info-700',
          )}
          message={message ?? ''}
        />
      </div>
    </div>
  );
};

export default TutorialMessage;
